import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormTextField } from '@skupreme/shared/ui/form';
import { useAuth } from '@contexts/auth';
import { config } from '@utility/config';
import { CommonUtility } from '@utility/common';
import { ConnectPopup } from './ConnectPopup';

const wcSchema = z.object({
  shop_url: z
    .string()
    .trim()
    .min(1, 'URL is required')
    .regex(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Enter correct URL',
    ),
});

type WCValues = z.infer<typeof wcSchema>;

const safeRedirect = (url: string) =>
  url.indexOf('https://') === -1 ? `https://${url}` : url;

type WCConnectPopupProps = {
  open: boolean;
  closeModal: () => void;
};

export function WCConnectPopup({ open, closeModal }: WCConnectPopupProps) {
  const { control, handleSubmit } = useForm<WCValues>({
    resolver: zodResolver(wcSchema),
    defaultValues: { shop_url: '' },
    shouldUnregister: true,
  });

  const { user } = useAuth();

  const onSubmit = ({ shop_url }: WCValues) => {
    const endpoint = 'wc-auth/v1/authorize';
    const safeUrl = safeRedirect(shop_url);
    const params = {
      app_name: 'Skupreme',
      scope: 'read_write',
      user_id: JSON.stringify({
        organization_id: user?.organization_id,
        shop_url: safeUrl,
      }),
      return_url: window.location.href,
      callback_url: config.wcCallbackURL,
    };
    const searchParams = CommonUtility.objectToParams(params).replace(
      /%20/g,
      '+',
    );
    window.open(`${safeUrl}/${endpoint}?${searchParams}`, '_blank');
  };

  return (
    <ConnectPopup
      open={open}
      title="Connect your WooCommerce Store"
      onClose={() => closeModal()}
      onSubmit={handleSubmit(onSubmit)}
    >
      <p className="sk:m-0 sk:text-fg-secondary">
        Enter your WooCommerce store&apos;s domain name below (Example:
        wordpress.skupreme.com)
      </p>
      <FormTextField
        control={control}
        name="shop_url"
        label="URL"
        placeholder="yourstore"
        isRequired
      />
    </ConnectPopup>
  );
}
